/* =============================================================
   ClinicalSkin — theme.js
   Vanilla, dependency-free. Modules: drawers, cart, product,
   gallery, facets, reveal, header, addresses.
   ============================================================= */
(function () {
  'use strict';

  var CFG = window.CSTheme || { routes: {}, strings: {}, lowStockThreshold: 5 };

  /* ---------- utils ---------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* =============================================================
     Drawers & overlay
     ============================================================= */
  var overlay = qs('[data-overlay]');
  var openDrawerEl = null;

  function lockScroll(lock) {
    document.body.classList.toggle('scroll-locked', lock);
  }

  function openDrawer(el) {
    if (!el) return;
    if (openDrawerEl && openDrawerEl !== el) closeDrawer(openDrawerEl, true);
    el.classList.add('is-open');
    el.removeAttribute('aria-hidden');
    openDrawerEl = el;
    if (overlay) { overlay.hidden = false; requestAnimationFrame(function () { overlay.classList.add('is-visible'); }); }
    lockScroll(true);
    var focusable = el.querySelector('[data-autofocus]') || el.querySelector('button, a, input');
    if (focusable) setTimeout(function () { focusable.focus({ preventScroll: true }); }, 120);
  }

  function closeDrawer(el, keepOverlay) {
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (openDrawerEl === el) openDrawerEl = null;
    if (!keepOverlay && overlay) {
      overlay.classList.remove('is-visible');
      setTimeout(function () { if (!openDrawerEl) overlay.hidden = true; }, 320);
      lockScroll(false);
    }
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(document.getElementById(opener.getAttribute('data-open')));
      return;
    }
    var closer = e.target.closest('[data-close]');
    if (closer) {
      e.preventDefault();
      closeDrawer(closer.closest('.drawer'));
      return;
    }
    if (e.target === overlay && openDrawerEl) closeDrawer(openDrawerEl);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openDrawerEl) closeDrawer(openDrawerEl);
  });

  /* =============================================================
     Cart
     ============================================================= */
  var Cart = {
    updateCount: function (count) {
      qsa('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.classList.toggle('has-items', count > 0);
      });
    },

    refresh: function (openAfter) {
      return Promise.all([
        fetch(CFG.routes.root + '?sections=cart-drawer').then(function (r) { return r.json(); }),
        fetch(CFG.routes.cart + '.js').then(function (r) { return r.json(); })
      ]).then(function (results) {
        var html = results[0]['cart-drawer'];
        var cart = results[1];
        var current = document.getElementById('shopify-section-cart-drawer');
        if (html && current) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.getElementById('shopify-section-cart-drawer');
          if (fresh) {
            var wasOpen = openDrawerEl && current.contains(openDrawerEl);
            current.replaceWith(fresh);
            if (wasOpen || openAfter) {
              openDrawerEl = null;
              openDrawer(document.getElementById('CartDrawer'));
            }
          }
        }
        Cart.updateCount(cart.item_count);
        return cart;
      });
    },

    add: function (body) {
      return fetch(CFG.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: body
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      });
    },

    change: function (line, quantity) {
      return fetch(CFG.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      }).then(function (r) { return r.json(); });
    }
  };

  function afterCartMutation() {
    if (CFG.template === 'cart') {
      window.location.reload();
      return Promise.resolve();
    }
    return Cart.refresh(false);
  }

  /* Product form submit (PDP + featured) */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    var btn = form.querySelector('[data-atc]');
    var errEl = form.querySelector('[data-form-error]');
    if (errEl) errEl.hidden = true;
    if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-disabled', 'true'); }

    Cart.add(new FormData(form))
      .then(function () {
        if (CFG.cartType === 'page') {
          window.location.href = CFG.routes.cart;
          return;
        }
        return Cart.refresh(true);
      })
      .catch(function (err) {
        if (errEl) {
          errEl.textContent = (err && err.description) || CFG.strings.cartError;
          errEl.hidden = false;
        }
      })
      .finally(function () {
        if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-disabled'); }
      });
  });

  /* Quick add buttons on cards */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    var label = btn.querySelector('.btn__label');
    var original = label ? label.textContent : '';
    if (label) label.textContent = CFG.strings.adding;
    btn.setAttribute('aria-disabled', 'true');

    var body = new FormData();
    body.append('id', btn.getAttribute('data-variant-id'));
    body.append('quantity', 1);

    Cart.add(body)
      .then(function () {
        if (CFG.cartType === 'page') { window.location.href = CFG.routes.cart; return; }
        return Cart.refresh(true);
      })
      .then(function () { if (label) label.textContent = original; })
      .catch(function () { if (label) label.textContent = CFG.strings.cartError; setTimeout(function () { if (label) label.textContent = original; }, 2200); })
      .finally(function () { btn.removeAttribute('aria-disabled'); });
  });

  /* Quantity steppers (generic) */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-btn]');
    if (!btn) return;
    var wrap = btn.closest('.qty');
    var input = wrap && wrap.querySelector('.qty__input');
    if (!input) return;
    var val = parseInt(input.value, 10) || 1;
    var min = parseInt(input.min, 10);
    if (isNaN(min)) min = 1;
    val = btn.getAttribute('data-qty-btn') === 'inc' ? val + 1 : val - 1;
    if (val < min) val = min;
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Cart line quantity change + remove */
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-line-qty]');
    if (!input) return;
    var line = parseInt(input.getAttribute('data-line'), 10);
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    Cart.change(line, qty).then(afterCartMutation);
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-remove-line]');
    if (!btn) return;
    e.preventDefault();
    Cart.change(parseInt(btn.getAttribute('data-line'), 10), 0).then(afterCartMutation);
  });

  /* =============================================================
     Variant picker
     ============================================================= */
  function initVariantPicker(picker) {
    var jsonEl = picker.querySelector('[data-variant-json]');
    if (!jsonEl) return;
    var variants;
    try { variants = JSON.parse(jsonEl.textContent); } catch (err) { return; }

    var section = picker.closest('[data-product-section]') || document;
    var form = section.querySelector('form[data-product-form]');
    var idInput = form && form.querySelector('input[name="id"]');
    var atcButtons = qsa('[data-atc]', section);
    var priceEls = qsa('[data-price]', section);
    var compareEls = qsa('[data-compare]', section);
    var priceWraps = qsa('[data-price-wrapper]', section);
    var stockEl = qs('[data-stock]', section);
    var threshold = CFG.lowStockThreshold || 5;

    function selectedOptions() {
      var values = [];
      qsa('[data-option-position]', picker).forEach(function (group) {
        var pos = parseInt(group.getAttribute('data-option-position'), 10);
        var checked = group.querySelector('input:checked');
        if (checked) values[pos - 1] = checked.value;
        var valueEl = group.querySelector('[data-option-value]');
        if (valueEl && checked) valueEl.textContent = checked.value;
      });
      return values;
    }

    function findVariant(values) {
      return variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === values[i]; });
      });
    }

    function update() {
      var variant = findVariant(selectedOptions());

      atcButtons.forEach(function (btn) {
        var label = btn.querySelector('.btn__label') || btn;
        if (!variant) {
          btn.disabled = true; label.textContent = CFG.strings.unavailable;
        } else if (!variant.available) {
          btn.disabled = true; label.textContent = CFG.strings.soldOut;
        } else {
          btn.disabled = false; label.textContent = CFG.strings.addToBag;
        }
      });

      if (!variant) return;

      if (idInput) idInput.value = variant.id;

      priceEls.forEach(function (el) { el.textContent = variant.price; });
      compareEls.forEach(function (el) {
        if (variant.compare) { el.textContent = variant.compare; el.hidden = false; }
        else { el.textContent = ''; el.hidden = true; }
      });
      priceWraps.forEach(function (el) { el.classList.toggle('price--sale', !!variant.compare); });

      if (stockEl) {
        if (variant.available && variant.qty !== null && variant.qty > 0 && variant.qty <= threshold) {
          stockEl.textContent = 'Only ' + variant.qty + ' left in stock';
          stockEl.hidden = false;
        } else {
          stockEl.hidden = true;
        }
      }

      if (variant.media) {
        var mediaItem = qs('[data-media-id="' + variant.media + '"]', section);
        if (mediaItem) activateMedia(section, variant.media, true);
      }

      if (window.history && history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    }

    picker.addEventListener('change', update);
    update();
  }

  /* =============================================================
     Product gallery
     ============================================================= */
  function activateMedia(section, mediaId, scrollTo) {
    var items = qsa('[data-media-item]', section);
    var thumbs = qsa('[data-thumb]', section);
    var target = null;

    items.forEach(function (item) {
      var match = item.getAttribute('data-media-id') === String(mediaId);
      item.classList.toggle('is-active', match);
      if (match) target = item;
    });
    thumbs.forEach(function (t) {
      t.setAttribute('aria-current', t.getAttribute('data-media-target') === String(mediaId) ? 'true' : 'false');
    });

    if (target && scrollTo && window.matchMedia('(max-width: 989px)').matches) {
      target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }

  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-thumb]');
    if (!thumb) return;
    var section = thumb.closest('[data-product-section]');
    activateMedia(section, thumb.getAttribute('data-media-target'), true);
  });

  /* =============================================================
     Sticky add-to-cart
     ============================================================= */
  function initStickyAtc() {
    var anchor = qs('[data-atc-anchor]');
    var bar = qs('[data-sticky-atc]');
    if (!anchor || !bar || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(anchor);

    var trigger = bar.querySelector('[data-sticky-submit]');
    if (trigger) {
      trigger.addEventListener('click', function () {
        var main = qs('form[data-product-form] [data-atc]');
        if (main && !main.disabled) main.click();
      });
    }
  }

  /* =============================================================
     Scroll reveal + header
     ============================================================= */
  function initReveal() {
    var els = qsa('.reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('in'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -6% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  function initHeader() {
    var header = qs('[data-header]');
    if (!header) return;
    var update = function () { header.classList.toggle('is-scrolled', window.scrollY > 8); };
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* =============================================================
     Collection facets
     ============================================================= */
  function initFacets() {
    var form = document.getElementById('FacetsForm');
    if (!form) return;
    form.addEventListener('change', function (e) {
      if (e.target.closest('[data-no-submit]')) return;
      form.submit();
    });
  }

  /* =============================================================
     Product recommendations
     ============================================================= */
  function initRecommendations() {
    var holder = qs('[data-recommendations]');
    if (!holder) return;
    fetch(holder.getAttribute('data-url'))
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim().length) {
          holder.innerHTML = fresh.innerHTML;
          initReveal();
        } else {
          var sectionEl = holder.closest('.shopify-section');
          if (sectionEl) sectionEl.style.display = 'none';
        }
      })
      .catch(function () {});
  }

  /* =============================================================
     Address country / province selectors
     ============================================================= */
  function initAddressSelectors() {
    qsa('[data-country-select]').forEach(function (country) {
      var wrapper = document.getElementById(country.getAttribute('data-province-wrapper'));
      var province = wrapper && wrapper.querySelector('select');

      function populate() {
        if (!province) return;
        var option = country.options[country.selectedIndex];
        var raw = option ? option.getAttribute('data-provinces') : null;
        var provinces = [];
        try { provinces = JSON.parse(raw || '[]'); } catch (err) {}
        province.innerHTML = '';
        if (!provinces.length) { wrapper.hidden = true; return; }
        provinces.forEach(function (p) {
          var opt = document.createElement('option');
          opt.value = p[0];
          opt.textContent = p[1];
          province.appendChild(opt);
        });
        var def = province.getAttribute('data-default');
        if (def) province.value = def;
        wrapper.hidden = false;
      }

      var defCountry = country.getAttribute('data-default');
      if (defCountry) country.value = defCountry;
      country.addEventListener('change', populate);
      populate();
    });
  }

  /* =============================================================
     Boot
     ============================================================= */
  document.addEventListener('DOMContentLoaded', function () {
    qsa('[data-variant-picker]').forEach(initVariantPicker);
    initStickyAtc();
    initReveal();
    initHeader();
    initFacets();
    initRecommendations();
    initAddressSelectors();
  });
})();
